import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlayCircle, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { PageHero, CtaBand } from "@/components/blocks";
import { BrandImage } from "@/components/BrandImage";
import { ArticleCard } from "@/components/ArticleCard";
import { Reveal } from "@/components/Reveal";
import { COMPANY } from "@/data/site";
import { categorySlug } from "@/data/techAdvice";
import { breadcrumbJsonLd } from "@/lib/seo";

export default function TechAdviceArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-site section-pad text-center" data-testid="tech-advice-article-not-found">
        <h1 className="h2-display mb-4">Article Not Found</h1>
        <p className="lead-text mb-6">This article may have been unpublished or moved.</p>
        <button onClick={() => navigate("/technology-advice")} className="btn-primary">BACK TO TECHNOLOGY ADVICE</button>
      </div>
    );
  }

  if (!article) return null;

  const publishedDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  const crumbs = [
    { label: "Technology Advice", to: "/technology-advice" },
    { label: article.category, to: `/technology-advice/category/${categorySlug(article.category)}` },
    { label: article.title },
  ];

  const origin = window.location.origin;
  const articleUrl = article.canonical_url || `${origin}/technology-advice/${article.slug}`;
  const imageUrl = article.featured_image
    ? (article.featured_image.startsWith("http") ? article.featured_image : `${origin}${article.featured_image}`)
    : `${origin}/cts-logo-full.png`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.meta_description || article.excerpt || undefined,
    image: [imageUrl],
    author: { "@type": "Organization", name: article.author || COMPANY.brand },
    publisher: {
      "@type": "Organization",
      name: COMPANY.brand,
      logo: { "@type": "ImageObject", url: `${origin}/cts-logo-full.png` },
    },
    datePublished: article.published_at || undefined,
    dateModified: article.updated_at || article.published_at || undefined,
    mainEntityOfPage: articleUrl,
  };

  const faqJsonLd = article.faqs?.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  return (
    <>
      <Seo
        title={article.seo_title || `${article.title} | CTS Technology Advice`}
        description={article.meta_description || article.excerpt}
        canonical={article.canonical_url || undefined}
        image={article.featured_image}
        jsonLd={[articleJsonLd, faqJsonLd, breadcrumbJsonLd(crumbs)]}
      />
      <PageHero
        eyebrow={article.category}
        title={article.title}
        intro={article.excerpt}
        crumbs={crumbs}
      />

      <article className="section-pad !pt-0" data-testid="tech-advice-article-body">
        <div className="container-site max-w-3xl">
          <div className="flex items-center gap-3 text-sm text-cts-charcoal/70 mb-6" data-testid="article-meta-row">
            {article.author && <span>{article.author}</span>}
            {publishedDate && <span>• {publishedDate}</span>}
          </div>

          <BrandImage src={article.featured_image} alt={article.title} className="w-full h-64 md:h-96 rounded-xl mb-8" testId="article-featured-image" />

          <div>
            {article.content.split(/\n\s*\n/).map((para, i) => (
              <p key={i} className="lead-text !text-cts-charcoal mb-5">{para}</p>
            ))}
          </div>

          {article.faqs?.length > 0 && (
            <div className="mt-12" data-testid="article-faqs">
              <h2 className="h3-display mb-6">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {article.faqs.map((f, i) => (
                  <Reveal key={i} delay={i * 60}>
                    <div className="card-base p-5" data-testid={`article-faq-${i}`}>
                      <p className="font-heading font-bold text-cts-ink mb-2">{f.question}</p>
                      <p className="text-[15px] leading-relaxed text-cts-charcoal">{f.answer}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {article.watch_learn?.length > 0 && (
            <div className="mt-12" data-testid="article-watch-learn">
              <h2 className="h3-display mb-2 flex items-center gap-2"><PlayCircle size={22} className="text-cts-red" /> Watch &amp; Learn</h2>
              <p className="text-sm text-cts-charcoal/70 mb-6">Carefully selected external resources to help you learn more. These links open on other websites.</p>
              <div className="space-y-3">
                {article.watch_learn.map((v, i) => (
                  <a
                    key={i}
                    href={v.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`article-watch-item-${i}`}
                    aria-label={`${v.resource_type === "Official Documentation" ? "View" : "Watch"} ${v.title} (opens in a new tab, leaves the CTS website)`}
                    className="card-base flex items-center justify-between gap-4 p-5 hover:!border-cts-red"
                  >
                    <div>
                      {v.resource_type && (
                        <span className="inline-block text-[11px] font-heading font-bold uppercase tracking-wide text-cts-red bg-cts-redlight rounded px-2 py-0.5 mb-2">{v.resource_type}</span>
                      )}
                      <p className="font-heading font-bold text-cts-ink">{v.title}</p>
                      {v.creator && <p className="text-sm text-cts-charcoal/70">{v.creator}{v.duration ? ` • ${v.duration}` : ""}</p>}
                      {v.description && <p className="text-sm text-cts-charcoal mt-1">{v.description}</p>}
                      <span className="link-arrow mt-2 !text-cts-red text-xs font-bold uppercase">
                        {v.resource_type === "Official Documentation" ? "VIEW RESOURCE" : "WATCH VIDEO"}
                      </span>
                    </div>
                    <ArrowUpRight size={20} className="text-cts-red shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {article.related_articles?.length > 0 && (
        <section className="section-pad bg-cts-soft" data-testid="article-related">
          <div className="container-site">
            <h2 className="h3-display mb-8">Related Articles</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {article.related_articles.map((a, i) => <ArticleCard key={a.slug} article={a} i={i} />)}
            </div>
          </div>
        </section>
      )}

      <CtaBand title="Have a Question About This?" requirement="Other" />
    </>
  );
}
